/**
 * Newsletter signup view for public site - NOS-56.
 * Renders inline email input + CTA, submits to submit-contact-form edge function.
 * Creates/updates contact with source form_submission.
 */

import { useState, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import { FunctionsHttpError } from "@supabase/supabase-js"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

export interface NewsletterSignupViewProps {
  orgId: string
  sectionId?: string
  heading?: string
  body?: string
  ctaText?: string
  placeholder?: string
  primaryColor?: string
}

export function NewsletterSignupView({
  orgId,
  sectionId,
  heading = "Subscribe to our newsletter",
  body,
  ctaText = "Subscribe",
  placeholder = "Enter your email",
  primaryColor = "var(--primary-color, #3b82f6)",
}: NewsletterSignupViewProps) {
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const trimmed = email.trim()
      if (!trimmed) {
        setError("Email is required")
        return
      }
      if (loading || submitted) return

      setLoading(true)
      setError(null)

      try {
        const { error: fnError } = await supabase.functions.invoke(
          "submit-contact-form",
          {
            body: {
              org_id: orgId,
              form_data: { email: trimmed },
              page_section_id: sectionId || undefined,
            },
          }
        )

        if (fnError) {
          let message = fnError.message ?? "Failed to subscribe"
          if (fnError instanceof FunctionsHttpError && fnError.context) {
            try {
              const res = fnError.context as Response
              const body = await res.json() as { error?: string }
              if (typeof body?.error === "string") message = body.error
            } catch {
              // keep default message
            }
          }
          setError(message)
          return
        }

        setSubmitted(true)
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unexpected error occurred")
      } finally {
        setLoading(false)
      }
    },
    [orgId, sectionId, email, loading, submitted]
  )

  if (submitted) {
    return (
      <div className="space-y-4">
        {heading && (
          <h2 className="text-2xl font-bold mb-2" style={{ color: primaryColor }}>
            {heading}
          </h2>
        )}
        <p className="text-muted-foreground rounded-lg bg-green-50 dark:bg-green-950/30 p-4">
          Thanks for subscribing! We&apos;ll be in touch soon.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {heading && (
        <h2 className="text-2xl font-bold mb-2" style={{ color: primaryColor }}>
          {heading}
        </h2>
      )}
      {body && <p className="text-muted-foreground text-sm mb-4">{body}</p>}
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
        <Input
          type="email"
          placeholder={placeholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
          className="flex-1 min-w-0"
        />
        <Button
          type="submit"
          disabled={loading}
          style={{ backgroundColor: primaryColor }}
          className="shrink-0"
        >
          {loading ? "Subscribing..." : ctaText}
        </Button>
      </form>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
